import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { TrendUp, Trophy, ShieldCheck, Lightning } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, formatApiError } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f4f5] flex items-stretch">
      {/* Left: brand panel */}
      <div className="hidden lg:flex w-1/2 bg-[#09090b] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-[0.06]" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-3 border-2 border-white px-3 py-2" data-testid="brand-mark">
            <div className="w-8 h-8 bg-[#2563eb] border-2 border-white" />
            <span className="font-display font-black tracking-tight text-xl">CFN</span>
          </div>
          <h1 className="font-display font-black text-5xl xl:text-6xl leading-[0.95] mt-20 tracking-tighter">
            CREATIVE<br />FINANCIAL<br /><span className="text-[#2563eb]">NETWORK</span>
          </h1>
          <p className="mt-8 max-w-md text-zinc-300 text-lg leading-relaxed">
            Track carrier appointments, comp levels, and sales activity.
            Compete. Climb the board. Close more.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-2 gap-4">
          {[
            { icon: ShieldCheck, label: "11 CARRIERS" },
            { icon: TrendUp, label: "LIVE METRICS" },
            { icon: Trophy, label: "LEADERBOARD" },
            { icon: Lightning, label: "INSTANT POINTS" },
          ].map((f, i) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.06 }}
              className="border-2 border-white px-4 py-3 flex items-center gap-3"
            >
              <f.icon size={22} weight="bold" />
              <span className="font-bold text-sm tracking-widest">{f.label}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="brut w-full max-w-md p-8 bg-white"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 bg-[#09090b]" />
            <span className="font-display font-black text-xl tracking-tight">SIGN IN</span>
          </div>
          <p className="text-zinc-600 mb-8 text-sm">Enter your CFN credentials.</p>

          <div className="space-y-5">
            <div>
              <Label className="font-bold uppercase tracking-widest text-xs">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 border-2 border-black rounded-none h-12 focus-visible:ring-0 focus-visible:ring-offset-0 focus:shadow-[4px_4px_0_0_#09090b]"
                placeholder="you@cfn.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label className="font-bold uppercase tracking-widest text-xs">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 border-2 border-black rounded-none h-12 focus-visible:ring-0 focus-visible:ring-offset-0 focus:shadow-[4px_4px_0_0_#09090b]"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-none bg-[#2563eb] hover:bg-[#1d4ed8] text-white border-2 border-black font-display font-black text-base tracking-wide shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 active:shadow-none active:translate-y-1 active:translate-x-1 transition-all"
              data-testid="login-submit"
            >
              {submitting ? "SIGNING IN…" : "SIGN IN →"}
            </Button>
          </div>

          <div className="mt-8 pt-6 border-t-2 border-dashed border-zinc-300">
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Demo Accounts</div>
            <div className="space-y-1 text-sm text-zinc-700">
              <div><span className="font-bold">Admin:</span> admin@cfn.com / cfnadmin</div>
              <div><span className="font-bold">Mgr Partner:</span> mp@cfn.com / cfnmp</div>
              <div><span className="font-bold">Agent:</span> agent@cfn.com / cfnagent</div>
            </div>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
